import React, { useState, useEffect } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { TextInput, RadioInput, SelectInput } from '../../../Helpers/Inputs';
import States from '../../../Helpers/Lists/States';
import Summary from '../../Common/Appointment/Summary';
import HelpSection from '../../Common/Appointment/HelpSection';
import CovrButton from '../../../Helpers/Buttons/CovrButton';
import { InputMaskPhone } from "../../../Helpers/Formatters";
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../../../sessionStorage';
import '../Haven.scss';

export default function LGAApplyDialog(props) {

  const { quoteFields, basicInfo, quoteResult } = props;

  const formMethods = useForm({
    resolver: yupResolver(LGAApplyDialog.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
  });

  const { handleSubmit, register, setValue } = formMethods;

  // inputs 
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastFourSSN, setlastFourSSN] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState(props.basicInfo.state || '');
  const [zip, setZip] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [email, setEmail] = useState(props.basicInfo.email || '');
  const [phoneNumberType, setPhoneNumberType] = useState('');

  useEffect(() => {
    register('state');
    setValue('state', state);
    register('phoneNumberType');
    setValue('phoneNumberType', phoneNumberType);
  }, [register]);

  const handleValueChange = (field, value, useStateSetValue) => {
    setValue(field, value);
    useStateSetValue(value);
  };

  const onSubmit = () => {
    setLoading(true);
    props.logQuote(email, null, props.quoteResult);

    const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

    const submissionBody = {
      "firstName": firstName,
      "lastName": lastName,
      "dob": basicInfo.birthdate,
      "gender": basicInfo.gender,
      "ssn": lastFourSSN,
      "addressLine1": streetAddress,
      "city": city,
      "state": state,
      "zipCode": zip,
      "insuranceAmount": quoteFields.coverageAmount,
      "coveragePeriod": quoteFields.termLength,
      "insurancePremium": quoteResult.monthlyRate,
      "waiveOffPremiumRider": false,
      "primaryPhoneNumber": primaryPhone,
      "primaryPhoneLocation": phoneNumberType,
      "email": email,
      "product": `OPTerm ${quoteFields.termLength}`,
      "uwClass": quoteFields.healthClass,
      "tobacco": basicInfo.tobacco,
    };

    // make lga submit call
    fetch(`${covrServicesUrl}/api/LGA/LGAHandoffSubmit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
      },
      body: JSON.stringify(submissionBody),
    })
      .then(res => res.json())
      .then(json => {
        console.log(json);

        if (json.success === true) {
          var redirect = json.data.loginUrl;
          window.location = redirect;
        }
        else if (json.success === false) {
          setLoading(false);
        }
      })
  }

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="lgaModalInputs">
          <Col xs={12} md={6}>
            <h4 className="mb-4">Your Information</h4>
            <div className='mb-3'>
              <TextInput
                name="firstName"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                label="First Name"
              />
            </div>

            <div className='mb-3'>
              <TextInput
                name="lastName"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                label="Last Name"
              />
            </div>

            <div className='mb-3'>
              <TextInput
                name="lastFourSSN"
                id="lastFourSSN"
                value={lastFourSSN}
                onChange={(e) => setlastFourSSN(e.target.value)}
                label="Last Four Digits of Your Social"
                InputProps={{
                  endAdornment: <Tooltip title={'The last four digits of your SS# is used to verify identity and helps expedite your life insurance application'} placement="top"><InfoIcon /></Tooltip>
                }}
              />
            </div>

            <div className="mb-3">
              <b>Address</b>
            </div>

            <div className='mb-3'>
              <TextInput
                name="streetAddress"
                id="streetAddress"
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                label="Street Address"
              />
            </div>

            <div className='mb-3'>
              <TextInput
                name="city"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                label="City"
              />
            </div>

            <Row>
              <Col>
                <div className='mb-3'>
                  <SelectInput
                    name="state"
                    id="state"
                    value={state}
                    onChange={(e) => handleValueChange('state', e.target.value, setState)}
                    label="State"
                    selectOptions={States()}
                  />
                </div>
              </Col>

              <Col>
                <div className='mb-3'>
                  <TextInput
                    name="zip"
                    id="zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    label="Zip"
                  />
                </div>
              </Col>
            </Row>

            <div className='mb-3'>
              <TextInput
                name="primaryPhone"
                id="primaryPhone"
                value={primaryPhone}
                onChange={(e) => setPrimaryPhone(e.target.value)}
                label="Primary Phone"
                InputProps={{
                  inputComponent: InputMaskPhone,
                }}
              />
            </div>

            <div className='mb-3'>
              <RadioInput
                name="phoneNumberType"
                id="phoneNumberType"
                label="Phone Type"
                value={phoneNumberType}
                radios={[{ value: "Cell", label: "Cell Phone" }, { value: "Work", label: "Work Phone" }]}
                onChange={e => handleValueChange('phoneNumberType', e.target.value, setPhoneNumberType)}
              />
            </div>


            <div className='mb-3'>
              <TextInput
                name="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                label="Email"
              />
            </div>

            <div className='mb-3'>
              <CovrButton
                name="START APPLICATION"
                loading={loading}
              />
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Summary
              title={'Quote Summary'}
              coverageAmount={quoteFields.coverageAmount}
              carrier={'LGA'}
              termLength={`${quoteFields.termLength} Year Term`}
              premium={quoteResult.monthlyRate}
              basicInfo={basicInfo}
              carrierLogoUrl='/logos/LGA-logo.png'
            />
            <div className="large-screen">
              <HelpSection />
            </div>
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
}

LGAApplyDialog.propTypes = {
  logQuote: PropTypes.func.isRequired,
  quoteResult: PropTypes.object.isRequired,
};

LGAApplyDialog.ValidationSchema = object().shape({
  email: string()
    .email('Email address must be valid.')
    .required('Email is required'),
  firstName: string()
    .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
    .required('First Name is required.'),
  lastName: string()
    .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
    .required('Last Name is required.'),
  city: string()
    .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
    .required('City is required.'),
  state: string().required('State is required.'),
  streetAddress: string().required('Street Address is required.'),
  primaryPhone: string()
    .required('Phone number is required.')
    .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
  zip: string()
    .required('Zip code is required.')
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
  phoneNumberType: string().required('Phone number type is required.'),
  lastFourSSN: string()
    .required('Last four of SSN are required.')
    .matches(/^[0-9]{4}$/, 'Last four of SSN must be 4 number characters.')
});
